/**
 * Context-preserving back navigation.
 *
 * The problem
 * -----------
 * Detail pages (fee, player, edit, mark-as-paid) can be reached from many
 * different origins. Hardcoding "Back to Fees" or "Back to Payments" on a
 * detail page means users who navigated in from a Team detail or from the
 * Dashboard get bounced away from where they were working. Relying on
 * browser history (`navigate(-1)`) is also unreliable — refreshes, deep
 * links, notification links, and email links all break it.
 *
 * The pattern
 * -----------
 * When a caller links into a detail page, it appends a `?from=…` (and
 * optional `?from_label=…`) query param that captures the URL + human
 * label of the origin. The detail page's Back button reads that param
 * and routes there, falling back to a sensible parent (usually the
 * top-level tab) when the origin can't be determined.
 *
 * Only same-origin, single-slash-prefixed paths are honored. Anything
 * else silently falls back — so a bad hand-crafted URL can never redirect
 * a coach off-site or into an internal admin route they don't own.
 *
 * Usage
 * -----
 *   // Caller (linking IN to a detail page)
 *   import { withReturnTo } from "@/lib/backNavigation";
 *   <Link to={withReturnTo(`/coach/charges/${id}`, { from: "/coach/teams/" + tid, label: teamName })} />
 *
 *   // Detail page (Back button)
 *   import { useSmartBack } from "@/lib/backNavigation";
 *   const back = useSmartBack("/coach/charges", "Fees");
 *   <Link to={back.to}><ArrowLeft /> Back to {back.label}</Link>
 */

import { useLocation } from "react-router-dom";

// Whitelist of route prefixes we're willing to route Back to. Prevents an
// attacker-crafted or truncated `?from=` value from redirecting the user
// off-site or into a route they shouldn't reach.
//
// Only in-app surfaces are allowed — the landing page ('/') is not a
// natural back destination from a detail page, so we don't include it.
const SAFE_PREFIXES = [
  "/coach/",
  "/parent/",
  "/admin/",
  "/app",
];

const isSafeReturnPath = (p) => {
  if (typeof p !== "string" || p.length === 0) return false;
  if (!p.startsWith("/")) return false;
  if (p.startsWith("//")) return false;      // protocol-relative escape
  if (p.includes("://")) return false;       // absolute URL escape
  // Match either the prefix exactly ("/app") or a nested path under it
  // ("/coach/teams/…"). The prefix-only startsWith check is intentional:
  // "/coach" alone should NOT match because it's not a routable page on
  // its own — only /coach/… is.
  return SAFE_PREFIXES.some((pre) =>
    p === pre || p.startsWith(pre.endsWith("/") ? pre : pre + "/"),
  );
};

/**
 * Append `?from=…` and `?from_label=…` to a target URL. `from` and label
 * are optional — omitting them yields the plain destination.
 */
export function withReturnTo(dest, opts = {}) {
  if (!dest || typeof dest !== "string") return dest;
  const { from, label } = opts;
  if (!from) return dest;
  const enc = encodeURIComponent(from);
  const sep = dest.includes("?") ? "&" : "?";
  let out = `${dest}${sep}from=${enc}`;
  if (label) out += `&from_label=${encodeURIComponent(label)}`;
  return out;
}

/**
 * Hook: derive a Back target for a detail page. Reads `?from=…` +
 * `?from_label=…` off the current URL. If either is missing or unsafe,
 * falls back to the provided default.
 */
export function useSmartBack(defaultTo, defaultLabel) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const rawFrom = params.get("from");
  const rawLabel = params.get("from_label");
  if (rawFrom && isSafeReturnPath(rawFrom)) {
    return { to: rawFrom, label: rawLabel || defaultLabel, isContextual: true };
  }
  return { to: defaultTo, label: defaultLabel, isContextual: false };
}

/**
 * Convenience: build the current page URL (path + search) so a caller
 * can pass it as the `from` for a nested link — useful when the caller
 * itself was reached via a `?from=…` chain and we want the leaf detail
 * page's Back to return here.
 */
export function currentPathAsFrom(location) {
  if (!location) return "/";
  const p = location.pathname || "/";
  const s = location.search || "";
  return `${p}${s}`;
}
